import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useHttp from "../../../../../hooks/use-http";
import convertToExcel from "../../../../../components/ExportToExcel";
import Pagination from "../../../../../components/Pagination";
import { AutoComplete } from "antd";
import { toast } from "react-toastify";
import { formatNumber } from "../../../../../components/formatNumber";

const FarmerSummaryReport = () => {
  const navigate = useNavigate();
  const handleButtonClick = () => {
    navigate("/farmerwise");
  };

  const [seasonlist, setSeasonlist] = useState(null);
  const [seasonId, setSeasonId] = useState("");
  const [ranchId, setRanchId] = useState("");
  const [ranchData, setRanchData] = useState([]);
  const [fieldName, setFieldName] = useState("");
  const [cropId, setCropId] = useState("");
  const [cropData, setCropData] = useState([]);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [farmerNumber, setFarmerNumber] = useState("");
  const [farmerData, setFarmerData] = useState();
  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = Number(localStorage.getItem("pageSize")) || 10;
  const [currentPage, setCurrentPage] = useState(1);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const { sendRequest: getSeason } = useHttp();
  useEffect(() => {
    getSeason(
      { url: `/api/seasons/?orgId=${localStorage.getItem("orgId")}` },
      (data) => {
        setSeasonlist(data);
      }
    );
  }, [getSeason]);

  const { sendRequest: getCropData } = useHttp();
  useEffect(() => {
    getCropData(
      { url: `/api/farmerCropList/?orgId=${localStorage.getItem("orgId")}` },
      (data) => {
        setCropData(data.output || []);
      }
    );
  }, [getCropData]);

  const { sendRequest: getRanch } = useHttp();
  useEffect(() => {
    getRanch(
      { url: `/api/ranchDetail/?orgId=${localStorage.getItem("orgId")}` },
      (data) => {
        setRanchData(data);
      }
    );
  }, [getRanch]);

  const { sendRequest: getFarmerNumber } = useHttp();
  useEffect(() => {
    var url = "";
    if (seasonId > 0) {
      url = url + "&seasonId=" + seasonId;
    }
    if (ranchId > 0) {
      url = url + "&ranchId=" + ranchId;
    }
    getFarmerNumber(
      {
        url: `/api/agronomyFarmerNumberList/?orgId=${localStorage.getItem(
          "orgId"
        )}&empId=${localStorage.getItem("empId")}${url}`,
      },
      (data) => {
        setFarmerData(data);
      }
    );
  }, [getFarmerNumber, ranchId, seasonId]);

  const handleSearch = (value) => {
    setFarmerNumber(value);
    setData([]);
    setTotalItems(0);
  };

  const filteredOptions =
    farmerData &&
    farmerData
      .filter((item) => item.farmerNo.includes(farmerNumber))
      .map((item) => ({ value: item.farmerNo }));

  // separate hook instances so simultaneous calls never clash
  const { sendRequest: getBudgetData } = useHttp();
  const { sendRequest: getExportData } = useHttp();

  const buildFilterUrl = () => {
    var urlfilter = "";
    if (seasonId > 0) urlfilter += `&seasonId=${seasonId}`;
    if (ranchId > 0) urlfilter += `&ranchId=${ranchId}`;
    if (fieldName && fieldName !== "") urlfilter += `&fieldId=${fieldName}`;
    if (farmerNumber) urlfilter += `&farmerNo=${farmerNumber}`;
    if (cropId && cropId !== "") urlfilter += `&cropId=${cropId}`;
    return urlfilter;
  };

  const baseUrl = () =>
    `/api/farmerSummaryReport/?orgId=${localStorage.getItem(
      "orgId"
    )}&empId=${localStorage.getItem("empId")}`;

  // fetch ONE page from the server
  const fillBudgetData = () => {
    getBudgetData(
      {
        url: `${baseUrl()}${buildFilterUrl()}&page=${currentPage}&pageSize=${itemsPerPage}`,
      },
      (res) => {
        setData(res.farmerData || []);
        setTotalItems(res.total || 0);
      }
    );
  };

  // refetch whenever a filter OR the page changes
  useEffect(() => {
    fillBudgetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonId, ranchId, fieldName, cropId, currentPage]);

  // Field Name dropdown: built from the loaded rows (same as Farmer Budget
  // report). Options are merged, so picking one field doesn't shrink the list
  // to just that field; the list is cleared when the ranch changes.
  useEffect(() => {
    if (!data || data.length === 0) return;

    let rows = data;
    if (ranchId && ranchId > 0) {
      const selectedRanch = ranchData.find(
        (x) => String(x.ranchId) === String(ranchId)
      );
      if (selectedRanch) {
        rows = data.filter((item) => item.ranchName === selectedRanch.ranchName);
      }
    }

    const codes = rows.map((item) => item.fieldCode).filter(Boolean);
    if (codes.length === 0) return;

    setFieldOptions((prev) =>
      [...new Set([...prev.map((p) => p.fieldCode), ...codes])]
        .sort()
        .map((f) => ({ fieldCode: f }))
    );
  }, [ranchId, ranchData, data]);

  const fieldsToExport = [
    "farmerNo",
    "farmerName",
    "executiveName",
    "gender",
    "ranchName",
    "fieldCode",
    "plotArea",
    "cropName",
    "seasonName",
    "inputCostActual",
    "inputCostMarkup",
    "labourCostActual",
    "labourCostMarkup",
    "harvest",
    "programmatic",
    "totalCost",
    "farmerTotalHarvest",
    "loanPayment",
    "farmerActualPayment",
    "loanBalance",
  ];

  const customHeaders = [
    "Farmer No",
    "Farmer Name",
    "Executive Name",
    "Gender",
    "Ranch Name",
    "Field Name",
    "Plot Area",
    "Crop",
    "Season Name",
    "Cost of Inputs - Actual",
    "Cost of Inputs - Markup",
    "Cost of Machine Labor - Actual",
    "Cost of Machine Labor - Markup",
    "Harvest",
    "Programmatic",
    "Total Cost",
    "Farmers Total harvest",
    "Loan Payment",
    "Farmers actual payment",
    "Loan Balance",
  ];

  // Export needs ALL rows (not just the current page), so ask the server
  // for the full list with export=1.
  const handleExportExcel = () => {
    if (!totalItems) {
      toast.error("No data available to export!");
      return;
    }
    getExportData(
      { url: `${baseUrl()}${buildFilterUrl()}&export=1` },
      (res) => {
        const rows = res.farmerData || [];
        if (rows.length === 0) {
          toast.error("No data available to export!");
          return;
        }
        convertToExcel(
          rows,
          fieldsToExport,
          "Farmer Summary Report.xlsx",
          customHeaders
        );
      }
    );
  };

  // any filter change goes back to page 1
  const resetPage = () => setCurrentPage(1);

  return (
    <>
      <div className="content-wrapper">
        <div className="row mx-2">
          <div className="col-md-8 mt-3">
            <label className="card-title">Farmer Summary</label>
          </div>
          <div className="col-md-4 text-right mt-3">
            <button
              type="button"
              className="btn btn-tool"
              data-card-widget="remove"
              onClick={handleButtonClick}
            >
              <i className="fas fa-times" />
            </button>
          </div>
        </div>
        <br />
        <section className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-3">
                <div className="form-group">
                  <label>Crop Name</label>
                  <select
                    className="form-control"
                    value={cropId}
                    onChange={(e) => {
                      setCropId(e.target.value);
                      resetPage();
                    }}
                  >
                    <option value="">Select</option>
                    {cropData &&
                      cropData.map((x) => (
                        <option key={x.CropId} value={x.CropId}>
                          {x.CropName}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group">
                  <label>Season</label>
                  <select
                    id="season"
                    className="form-control"
                    value={seasonId}
                    onChange={(e) => {
                      setSeasonId(e.target.value || "");
                      resetPage();
                    }}
                  >
                    <option value="">Select</option>
                    {seasonlist &&
                      seasonlist.map((x) => (
                        <option key={x.seasonId} value={x.seasonId}>
                          {x.seasonName}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group">
                  <label>Ranch</label>
                  <select
                    id="ranch"
                    className="form-control"
                    value={ranchId}
                    onChange={(e) => {
                      setRanchId(e.target.value || "");
                      setFieldName("");
                      setFieldOptions([]);
                      resetPage();
                    }}
                  >
                    <option value="">Select</option>
                    {ranchData &&
                      ranchData.map((x) => (
                        <option key={x.ranchId} value={x.ranchId}>
                          {x.ranchName}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group">
                  <label>Field Name</label>
                  <select
                    className="form-control"
                    value={fieldName}
                    onChange={(e) => {
                      setFieldName(e.target.value);
                      resetPage();
                    }}
                  >
                    <option value="">Select</option>
                    {fieldOptions.map((x, i) => (
                      <option key={i} value={x.fieldCode}>
                        {x.fieldCode}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-md-3">
                <label htmlFor="farmer_number">Farmer Number</label>
                <AutoComplete
                  className="form-control"
                  style={{ height: "30px" }}
                  value={farmerNumber}
                  options={filteredOptions}
                  placeholder="Farmer Number"
                  onChange={handleSearch}
                />
              </div>
              <div className="col-md-3  text-right">
                <div className="form-group">
                  <label>&nbsp;</label>
                  <button
                    className="form-control btn btn-sm btn-primary"
                    onClick={() => {
                      if (currentPage !== 1) {
                        setCurrentPage(1); // effect will fetch page 1
                      } else {
                        fillBudgetData();
                      }
                    }}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
          <h5>Please select the parameters to get the desired result</h5>
          <hr />
        </section>

        <section className="content">
          <div className="container-fluid">
            <div className="text-right my-2">
              <button
                type="button"
                className="btn btn-sm btn-warning"
                onClick={handleExportExcel}
              >
                <i className="fas fa-file-excel"></i>
              </button>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-body" style={{ padding: "0.4rem" }}>
                    <table className="table table-bordered table-responsive text-center table-sm">
                      <thead>
                        <tr className="table-header">
                          <th>Farmer No</th>
                          <th>Farmer Name</th>
                          <th>Executive Name</th>
                          <th>Gender</th>
                          <th>Ranch Name</th>
                          <th>Field Name</th>
                          <th>Plot Area</th>
                          <th>Crop</th>
                          <th>Season Name</th>
                          <th>Cost of Inputs - Actual</th>
                          <th>Cost of Inputs - Markup</th>
                          <th>Cost of Machine Labor - Actual</th>
                          <th>Cost of Machine Labor - Markup</th>
                          <th>Harvest</th>
                          <th>Programmatic</th>
                          <th>Total Cost</th>
                          <th>Farmers Total harvest</th>
                          <th>Loan Payment</th>
                          <th>Farmers actual payment</th>
                          <th>Loan Balance</th>
                        </tr>
                      </thead>

                      <tbody>
                        {data.map((itm, i) => (
                          <tr key={i}>
                            <td>{itm.farmerNo}</td>
                            <td>{itm.farmerName}</td>
                            <td>{itm.executiveName}</td>
                            <td>{itm.gender}</td>
                            <td>{itm.ranchName}</td>
                            <td>{itm.fieldCode}</td>
                            <td>{formatNumber(itm.plotArea)}</td>
                            <td>{itm.cropName}</td>
                            <td>{itm.seasonName}</td>
                            <td>{formatNumber(itm.inputCostActual)}</td>
                            <td>{formatNumber(itm.inputCostMarkup)}</td>
                            <td>{formatNumber(itm.labourCostActual)}</td>
                            <td>{formatNumber(itm.labourCostMarkup)}</td>
                            <td>{formatNumber(itm.harvest)}</td>
                            <td>{formatNumber(itm.programmatic)}</td>
                            <td>{formatNumber(itm.totalCost)}</td>
                            <td>{formatNumber(itm.farmerTotalHarvest)}</td>
                            <td>{formatNumber(itm.loanPayment)}</td>
                            <td>{formatNumber(itm.farmerActualPayment)}</td>
                            <td>{formatNumber(itm.loanBalance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="card-footer clearfix">
                    <Pagination
                      totalItems={totalItems}
                      itemsPerPage={itemsPerPage}
                      onPageChange={paginate}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FarmerSummaryReport;